import { useEffect } from 'react';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@monksflow/monks-ui';
import { ChevronFirstIcon, ChevronLastIcon } from 'lucide-react';

import { getPageWindow } from './getPageWindow';
import type { PaginationControlsProps } from './PaginationControls.types';
import { useTranslation } from 'react-i18next';

export function PaginationControls({
  page,
  totalItems,
  pageSize,
  onPageChange,
  siblingCount,
  className,
}: PaginationControlsProps) {
  const { t } = useTranslation('common');

  const { pages, showLeadingEllipsis, showTrailingEllipsis, totalPages, current } = getPageWindow(
    page,
    totalItems,
    pageSize,
    siblingCount,
  );

  // Self-heal when the caller's page falls past the last page (e.g. the result
  // set shrank after a delete). `current` is only the clamped *display* value —
  // the caller still owns `page` and derives its data request from it, so we
  // must pull that source-of-truth state back in range or the request stays
  // stuck on an empty out-of-range window. Runs before the early return below
  // so it fires even when results collapse to a single hidden page.
  //
  // `no-use-effect` knowingly violated: this is a prop-driven reconciliation of
  // *parent* state, which a render-time setter can't do (React forbids updating
  // another component while rendering) and `useMountEffect` can't do (it must
  // re-run when page/totalItems change, not once on mount). A dependency-keyed
  // effect is the correct tool here.
  //
  // Only reconcile against a KNOWN, non-empty dataset. `totalItems === 0` is
  // ambiguous — it is also the transient state while the caller's query
  // refetches (its `data` is briefly undefined), and pulling the page back on
  // that would fight a legitimate page change mid-flight. A genuinely empty
  // result renders its own empty state, so leaving the page untouched is safe.
  useEffect(() => {
    if (totalItems > 0 && page > totalPages) {
      onPageChange(totalPages);
    }
  }, [page, totalItems, totalPages, onPageChange]);

  if (totalPages <= 1) {
    return null;
  }

  const isFirst = current <= 1;
  const isLast = current >= totalPages;

  const goTo = (target: number) => {
    const clamped = Math.min(Math.max(target, 1), totalPages);
    if (clamped !== current) {
      onPageChange(clamped);
    }
  };

  return (
    <Pagination className={className}>
      <PaginationContent>
        <PaginationItem>
          <PaginationLink
            aria-label={t('components.paginationControls.goToFirstPage')}
            aria-disabled={isFirst || undefined}
            tabIndex={isFirst ? -1 : undefined}
            className={isFirst ? 'pointer-events-none opacity-50' : undefined}
            onClick={() => goTo(1)}
          >
            <ChevronFirstIcon className="size-4" />
          </PaginationLink>
        </PaginationItem>

        <PaginationItem>
          <PaginationPrevious disabled={isFirst} onClick={() => goTo(current - 1)} />
        </PaginationItem>

        {showLeadingEllipsis && (
          <PaginationItem>
            <PaginationEllipsis />
          </PaginationItem>
        )}

        {pages.map((pageNumber) => (
          <PaginationItem key={pageNumber}>
            <PaginationLink isActive={pageNumber === current} onClick={() => goTo(pageNumber)}>
              {pageNumber}
            </PaginationLink>
          </PaginationItem>
        ))}

        {showTrailingEllipsis && (
          <PaginationItem>
            <PaginationEllipsis />
          </PaginationItem>
        )}

        <PaginationItem>
          <PaginationNext disabled={isLast} onClick={() => goTo(current + 1)} />
        </PaginationItem>

        <PaginationItem>
          <PaginationLink
            aria-label={t('components.paginationControls.goToLastPage')}
            aria-disabled={isLast || undefined}
            tabIndex={isLast ? -1 : undefined}
            className={isLast ? 'pointer-events-none opacity-50' : undefined}
            onClick={() => goTo(totalPages)}
          >
            <ChevronLastIcon className="size-4" />
          </PaginationLink>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
